import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Spinner, Text } from '@factorialco/deprecated-design-system'
import param from 'jquery-param'
import compact from 'lodash/compact'
import includes from 'lodash/includes'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import buildUrl from 'modules/core/lib/buildUrl'
import { currencyToNumber } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import legalEntities from 'modules/core/store/collections/legalEntities'
import users from 'modules/core/store/collections/users'

import { RESET_DATA } from 'modules/payroll_bundle/lib/constants'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { useSepa } from 'modules/payroll_bundle/lib/sepaContext'
import type BankAccount from 'modules/payroll_bundle/store/models/BankAccount'
import type Payment from 'modules/payroll_bundle/store/models/Payment'

interface IEmployee {
  amount: string
  userId: number
}

interface Props {
  employees: IEmployee[]
  selected: Array<number | null>
  bankAccounts: BankAccount[]
  payment?: Payment
  onClose: () => void
}

const GenerateSepaModal = ({ onClose, employees, selected, bankAccounts, payment }: Props) => {
  const { SEPADispatch } = useSepa()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { policyId, currentPeriodStatus, currentPeriod } = usePolicyPeriods()
  const currPeriodId = currentPeriod?.id
  const [legalEntityError, setLegalEntityError] = React.useState<string>('')

  const [accountId, setAccountId] = React.useState<number>(bankAccounts[0]?.attributes.id)

  const [legalEntityId, setLegalEntityId] = React.useState<number>(
    legalEntities.models[0]?.attributes.id
  )

  const [downloading, setDownloading] = React.useState<boolean>(false)

  const legalEntityMap = React.useRef(
    Object.fromEntries(
      legalEntities.models.map((le) => [
        le.attributes.id,
        {
          legal_name: le.attributes.legal_name,
          tin: le.attributes.tin,
        },
      ])
    )
  ).current

  const legalEntitiesOptions = Object.keys(legalEntityMap).map((legalEntityId) => {
    return {
      label: legalEntityMap[legalEntityId].legal_name,
      value: legalEntityId,
    }
  })

  const bankAccountsOptions = bankAccounts.map((ba) => {
    return {
      label: ba.attributes.bank_name,
      value: ba.attributes.id,
    }
  })

  const hasBankAccounts = bankAccountsOptions.length > 0
  const title = hasBankAccounts
    ? i18n.t('payroll_bundle.sepa_generator.matcher.payment_modal.title')
    : i18n.t('payroll_bundle.sepa_generator.matcher.payment_modal.no_bank_account.title')

  const description = hasBankAccounts
    ? i18n.t('payroll_bundle.sepa_generator.matcher.payment_modal.description')
    : undefined

  const button = hasBankAccounts
    ? i18n.t('payroll_bundle.sepa_generator.matcher.payment_modal.button')
    : i18n.t('payroll_bundle.sepa_generator.matcher.payment_modal.no_bank_account.configure')

  const cancel = i18n.t('payroll_bundle.sepa_generator.matcher.payment_modal.cancel')

  const legalEntityLabel = i18n.t(
    'payroll_bundle.sepa_generator.matcher.payment_modal.legal_entity_label'
  )

  const accountLabel = i18n.t('payroll_bundle.sepa_generator.matcher.payment_modal.account_label')

  const onGenerate = async () => {
    const employeesData = employees.map((e) => {
      if (!includes(selected, e.userId)) return null

      if (!e.amount || !e.userId) return null

      const employee = users?.find({
        id: e.userId,
      })?.employee

      return {
        employee_id: employee?.attributes.id,
        amount: currencyToNumber(e.amount),
      }
    })

    const payload = {
      account_id: accountId,
      legal_entity_id: legalEntityId,
      employees: [...compact(employeesData)],
      payment_id: payment?.id,
    }
    setDownloading(true)

    const api = ApiClient()

    const result = await asyncAction(() => api.post('/payroll/sepa/generate_sepa', payload), {
      successFlashTime: 8 * 1000,
    })

    if (result.success) {
      onClose()

      const paymentRouteLine = navigation.payrollBundle.policy.period.cycles.payments.fullPath({
        policyId: String(policyId),
        periodId: Number(currPeriodId),
      })

      navigate(paymentRouteLine)

      SEPADispatch({
        type: RESET_DATA,
      })

      const token = result.value.token
      window.location.href = buildUrl(
        `/payroll/sepa/download_sepa?${param({
          token,
        })}`
      )
      track(trackingEvents.payroll.generateSEPA, {
        period_status: currentPeriodStatus,
      })
    }
  }

  const onSepaCtaClick = () => {
    if (hasBankAccounts) {
      onGenerate()
    } else {
      navigate(navigation.settings.details.fullPath())
    }
  }

  const onLegalEntityChange = (value: string) => {
    setLegalEntityId(+value)

    if (legalEntityMap[value] && !legalEntityMap[value].tin) {
      setLegalEntityError(i18n.t('payroll_bundle.sepa_generator.errors.legal_entity_tin_not_found'))
    } else {
      setLegalEntityError('')
    }
  }

  const SelectModal = () => (
    <Modal
      illustration={hasBankAccounts ? 'credit-card' : 'alert'}
      title={title}
      description={description}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={onClose}>
            {cancel}
          </Button>
          <Button disabled={legalEntityError !== ''} onClick={() => onSepaCtaClick()}>
            {button}
          </Button>
        </Box>
      }
      onClose={onClose}
    >
      <Box gap="s12">
        {hasBankAccounts && (
          <>
            <SelectField
              label={accountLabel}
              value={String(accountId)}
              options={bankAccountsOptions}
              onChange={(v: string) => setAccountId(+v)}
            />
            <SelectField
              label={legalEntityLabel}
              value={String(legalEntityId)}
              options={legalEntitiesOptions}
              onChange={onLegalEntityChange}
              error={legalEntityError}
            />
          </>
        )}
        {!hasBankAccounts && (
          <Text size="300" weight="medium" color="grey800">
            {i18n.t('payroll_bundle.sepa_generator.matcher.payment_modal.no_bank_account.content')}
          </Text>
        )}
      </Box>
    </Modal>
  )

  const DownloadingModal = () => (
    <Modal
      illustration="download"
      title={i18n.t('payroll_bundle.sepa_generator.matcher.payment_modal.downloading.title')}
      description={i18n.t(
        'payroll_bundle.sepa_generator.matcher.payment_modal.downloading.description'
      )}
      onClose={onClose}
    >
      <Spinner />
    </Modal>
  )

  return downloading ? DownloadingModal() : SelectModal()
}

export default observer(GenerateSepaModal)
